import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { publicSupabase } from "@/integrations/supabase/publicClient";

const SITE_URL = "https://onlinesalespecialist.nl";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  noIndex?: boolean;
  schemaJson?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Centralized SEO head component.
 *
 * Page-level props supply the defaults. If an admin-managed `page_seo` row
 * exists for the current path it overrides the props (so editors can change
 * meta values without redeploying).
 */
export function SEOHead({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  noIndex,
  schemaJson,
}: SEOHeadProps) {
  const { pathname } = useLocation();

  const { data: dbSeo } = useQuery({
    queryKey: ["page-seo", pathname],
    queryFn: async () => {
      const { data, error } = await publicSupabase
        .from("page_seo")
        .select("*")
        .eq("page_path", pathname)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const finalTitle = dbSeo?.meta_title || title;
  const finalDescription = dbSeo?.meta_description || description;
  const finalCanonical =
    dbSeo?.canonical_url ||
    canonical ||
    `${SITE_URL}${pathname === "/" ? "" : pathname}`;
  const finalOgImage = dbSeo?.og_image || ogImage || DEFAULT_OG_IMAGE;
  const finalOgTitle = dbSeo?.og_title || finalTitle;
  const finalOgDescription = dbSeo?.og_description || finalDescription;
  const finalNoIndex = dbSeo?.noindex ?? noIndex ?? false;

  const schemas = schemaJson
    ? Array.isArray(schemaJson)
      ? schemaJson
      : [schemaJson]
    : [];

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={finalCanonical} />
      {finalNoIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={finalOgImage} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEOHead;
